import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@features/auth/api/auth.api';
import { useAuthStore } from '@features/auth/model/auth.store';
import { tokenStorage } from '@shared/api/token-storage';
import type { ApiError } from '@shared/api/api-error';
import type { LoginCredentials, Session } from '@features/auth/model/auth.types';

/** Read-only view of the session, for components that just need the admin. */
export function useAuth() {
  const status = useAuthStore((state) => state.status);
  const admin = useAuthStore((state) => state.admin);

  return {
    admin,
    status,
    isAuthenticated: status === 'authenticated',
    isRestoring: status === 'restoring',
  };
}

export function useLogin() {
  const setSession = useAuthStore((state) => state.setSession);
  const queryClient = useQueryClient();

  return useMutation<Session, ApiError, LoginCredentials>({
    mutationFn: authApi.login,
    onSuccess: (session) => {
      // Nothing cached under the previous session should survive a new login.
      queryClient.clear();
      setSession(session);
    },
  });
}

export function useLogout() {
  const clear = useAuthStore((state) => state.clear);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: authApi.logout,
    // The local session ends either way — a failed logout call must not strand
    // the admin in a half-signed-in state.
    onSettled: () => {
      clear();
      queryClient.clear();
    },
  });
}

/**
 * Validates a token found in storage on first paint. Until it resolves the store
 * sits in `restoring`, and the router shows a spinner rather than guessing.
 */
export function useRestoreSession(): void {
  const status = useAuthStore((state) => state.status);
  const setAdmin = useAuthStore((state) => state.setAdmin);
  const clear = useAuthStore((state) => state.clear);

  useEffect(() => {
    if (status !== 'restoring') {
      return;
    }

    if (!tokenStorage.read()) {
      clear();
      return;
    }

    let cancelled = false;

    authApi
      .me()
      .then((admin) => {
        if (!cancelled) {
          setAdmin(admin);
        }
      })
      .catch(() => {
        // A 401 is already handled by the interceptor; this covers the rest.
        if (!cancelled) {
          clear();
        }
      });

    return () => {
      cancelled = true;
    };
  }, [status, setAdmin, clear]);
}
